import { useEffect } from 'react';

interface SeoProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  schema?: any;
}

export function useSeo({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage = 'https://www.princeitsolution.com/assets/seo_img.jpg',
  ogType = 'website',
  schema,
}: SeoProps) {
  useEffect(() => {
    // 1. Set Document Title
    document.title = title;

    // Helper function to set or create meta element by attribute key and name
    const setMetaTag = (attrKey: string, attrVal: string, contentVal: string) => {
      let element = document.querySelector(`meta[${attrKey}="${attrVal}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attrKey, attrVal);
        document.head.appendChild(element);
      }
      element.setAttribute('content', contentVal);
    };

    // 2. Standard Meta Tags
    setMetaTag('name', 'description', description);
    if (keywords) {
      setMetaTag('name', 'keywords', keywords);
    }

    // 3. Resolve & Normalize Canonical URL
    const baseUrl = 'https://www.princeitsolution.com';
    let resolvedCanonical = canonicalUrl;
    if (!resolvedCanonical) {
      if (typeof window !== 'undefined') {
        const rawPath = window.location.pathname || '/';
        const cleanPath = rawPath === '/' ? '/' : rawPath.replace(/\/+$/, '');
        const aliasMap: Record<string, string> = {
          '/about': '/about-us',
          '/blogs': '/blog',
          '/contact': '/contact-us',
        };
        const mappedPath = aliasMap[cleanPath] || cleanPath;
        resolvedCanonical = mappedPath === '/' ? `${baseUrl}/` : `${baseUrl}${mappedPath}`;
      } else {
        resolvedCanonical = baseUrl;
      }
    }

    // 4. OpenGraph Meta Tags
    setMetaTag('property', 'og:title', title);
    setMetaTag('property', 'og:description', description);
    setMetaTag('property', 'og:url', resolvedCanonical);
    setMetaTag('property', 'og:image', ogImage);
    setMetaTag('property', 'og:type', ogType);

    // 5. Twitter Card Meta Tags
    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'twitter:title', title);
    setMetaTag('name', 'twitter:description', description);
    setMetaTag('name', 'twitter:image', ogImage);

    // 6. Ensure Exactly One Canonical URL Link (remove duplicates)
    const existingCanonicals = document.querySelectorAll('link[rel="canonical"]');
    if (existingCanonicals.length > 1) {
      for (let i = 1; i < existingCanonicals.length; i++) {
        existingCanonicals[i].remove();
      }
    }
    let linkCanonical = document.querySelector('link[rel="canonical"]');
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute('href', resolvedCanonical);

    // 6. Dynamic JSON-LD Schema
    let scriptSchema = document.querySelector('script#seo-schema');
    if (schema) {
      if (!scriptSchema) {
        scriptSchema = document.createElement('script');
        scriptSchema.setAttribute('type', 'application/ld+json');
        scriptSchema.setAttribute('id', 'seo-schema');
        document.head.appendChild(scriptSchema);
      }
      scriptSchema.innerHTML = JSON.stringify(schema);
    } else if (scriptSchema) {
      scriptSchema.remove();
    }

    return () => {
      if (scriptSchema) scriptSchema.remove();
    };
  }, [title, description, keywords, canonicalUrl, ogImage, ogType, schema]);
}
